import { Bounds, useBounds } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { type PropsWithChildren, useEffect } from 'react'
import * as THREE from 'three'

import { useZoomToObject } from '@components/storeMap/floor/3d/util'
import { useShelfRegistry } from '@components/storeMap/floor/ShelfRegistry'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'
import { isTestMode } from '@utils/env.ts'

import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'

function BoundsWrapper({ children }: PropsWithChildren) {
  return (
    <Bounds clip margin={10} maxDuration={isTestMode ? 0.01 : 2.5}>
      <BoundsChild>
        {children}
      </BoundsChild>
    </Bounds>
  )
}

function BoundsChild({ children }: PropsWithChildren) {
  const { registerZoomControls } = useStoreMapContext()
  const registry = useShelfRegistry<THREE.Object3D>()
  const zoomToObject = useZoomToObject()
  const bounds = useBounds()
  const controls = useThree(state => state.controls) as OrbitControlsImpl | null

  useEffect(() => {
    return registerZoomControls({
      zoomTo: (shelfId: string) => {
        const shelfObject = registry.get(shelfId)
        if (!shelfObject) return

        zoomToObject(shelfObject)
      },
      zoomIn: () => {
        if (!controls) return
        controls.dollyOut(1.2)
        controls.update()
      },
      zoomOut: () => {
        if (!controls) return
        controls.dollyIn(1.2)
        controls.update()
      },
      resetZoom: () => {
        bounds.refresh()
        bounds.fit()
      },
    })
  }, [registerZoomControls, registry, zoomToObject, bounds, controls])

  return children
}

export default BoundsWrapper